import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Input, NgModule } from '@angular/core';
import * as i1 from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
const DEFAULT_SIZE = 'small';
const ARIA_POLITENESS_ASSERTIVE = 'assertive';
/** required to be able to read same message multiple times https://github.com/angular/components/blob/7beb95219ddc9a16cd28ee2524bf1594e04c1c58/src/cdk/a11y/live-announcer/live-announcer.ts#L112-L127 */
const ANNOUNCER_DURATION = 1000;
class NxSpinnerComponent {
  /** Sets the size of the spinner. Default is 'small'. */
  set size(value) {
    if (value !== this._size) {
      this._size = value;
      this._cdr.markForCheck();
    }
  }
  get size() {
    return this._size;
  }
  /** Whether the spinner should use a negative styling. */
  set negative(value) {
    if (value !== this._negative) {
      this._negative = coerceBooleanProperty(value);
      this._cdr.markForCheck();
    }
  }
  get negative() {
    return this._negative;
  }
  /**
   * Message that will be announced by screen readers at instantiation.
   * Do __not__ combine with an `aria-live` area.
   * Empty messages will be ignored. Defaults to empty/no message
   */
  set activationAnnouncement(value) {
    this._activationAnnouncement = value;
  }
  get activationAnnouncement() {
    return this._activationAnnouncement;
  }
  /**
   * Message that will be announced by screen readers at destroy.
   * Do __not__ combine with an `aria-live` area.
   * Empty messages will be ignored. Defaults to empty/no message
   */
  set completionAnnouncement(value) {
    this._completionAnnouncement = value;
  }
  get completionAnnouncement() {
    return this._completionAnnouncement;
  }
  constructor(_cdr, liveAnnouncer) {
    this._cdr = _cdr;
    this.liveAnnouncer = liveAnnouncer;
    this._size = DEFAULT_SIZE;
    this._negative = false;
    this._activationAnnouncement = '';
    this._completionAnnouncement = '';
    /** Sets the aria live regions of the spinner. Default is 'assertive'. */
    this.ariaPoliteness = ARIA_POLITENESS_ASSERTIVE;
  }
  ngOnInit() {
    this.announce(this.activationAnnouncement);
  }
  ngOnDestroy() {
    this.announce(this.completionAnnouncement);
  }
  async announce(message) {
    if (message.length > 0) {
      await this.liveAnnouncer.announce(message, this.ariaPoliteness, ANNOUNCER_DURATION);
    }
  }
  static {
    this.ɵfac = function NxSpinnerComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSpinnerComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.LiveAnnouncer));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxSpinnerComponent,
      selectors: [["nx-spinner"]],
      hostVars: 11,
      hostBindings: function NxSpinnerComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("role", "status")("aria-live", ctx.ariaPoliteness)("aria-hidden", "true");
          i0.ɵɵclassProp("nx-spinner--small", ctx.size === "small")("nx-spinner--medium", ctx.size === "medium")("nx-spinner--large", ctx.size === "large")("nx-spinner--negative", ctx.negative);
        }
      },
      inputs: {
        size: "size",
        negative: "negative",
        activationAnnouncement: "activationAnnouncement",
        completionAnnouncement: "completionAnnouncement",
        ariaPoliteness: "ariaPoliteness"
      },
      decls: 3,
      vars: 0,
      consts: [[1, "nx-spinner__spin-block"], [1, "nx-spinner__edge-left"], [1, "nx-spinner__edge-right"]],
      template: function NxSpinnerComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0);
          i0.ɵɵelement(1, "div", 1)(2, "div", 2);
          i0.ɵɵelementEnd();
        }
      },
      styles: ["@keyframes _ngcontent-%COMP%_spin{0%{transform:rotate(45deg)}to{transform:rotate(405deg)}}[_nghost-%COMP%]{position:relative;display:block}.nx-spinner--small[_nghost-%COMP%]{width:16px;height:16px}.nx-spinner--medium[_nghost-%COMP%]{width:24px;height:24px}.nx-spinner--large[_nghost-%COMP%]{width:40px;height:40px}.nx-spinner__spin-block[_ngcontent-%COMP%]{width:100%;height:100%;transform:rotate(45deg);border-style:solid;border-radius:50%;border-color:var(--spinner-indicator-color) var(--spinner-background-color) var(--spinner-background-color) var(--spinner-background-color);animation-name:_ngcontent-%COMP%_spin;animation-duration:.8s;animation-iteration-count:infinite;animation-timing-function:linear}.nx-spinner--small[_nghost-%COMP%]   .nx-spinner__spin-block[_ngcontent-%COMP%]{border-width:1.6px}.nx-spinner--medium[_nghost-%COMP%]   .nx-spinner__spin-block[_ngcontent-%COMP%]{border-width:2.4px}.nx-spinner--large[_nghost-%COMP%]   .nx-spinner__spin-block[_ngcontent-%COMP%]{border-width:4px}.nx-spinner__edge-left[_ngcontent-%COMP%], .nx-spinner__edge-right[_ngcontent-%COMP%]{width:12%;height:12%;border-radius:50%;background-color:var(--spinner-indicator-color);position:absolute;top:4%}.nx-spinner--small[_nghost-%COMP%]   .nx-spinner__edge-left[_ngcontent-%COMP%], .nx-spinner--small[_nghost-%COMP%]   .nx-spinner__edge-right[_ngcontent-%COMP%]{width:10%;height:10%}.nx-spinner__edge-left[_ngcontent-%COMP%]{left:4%}.nx-spinner__edge-right[_ngcontent-%COMP%]{right:4%}.nx-spinner--negative[_nghost-%COMP%]   .nx-spinner__spin-block[_ngcontent-%COMP%]{border-color:var(--spinner-negative-indicator-color) var(--spinner-negative-background-color) var(--spinner-negative-background-color) var(--spinner-negative-background-color)}.nx-spinner--negative[_nghost-%COMP%]   .nx-spinner__edge-left[_ngcontent-%COMP%], .nx-spinner--negative[_nghost-%COMP%]   .nx-spinner__edge-right[_ngcontent-%COMP%]{background-color:var(--spinner-negative-indicator-color)}@media screen and (forced-colors: active){[_nghost-%COMP%]   .nx-spinner__spin-block[_ngcontent-%COMP%], .nx-spinner--negative[_nghost-%COMP%]   .nx-spinner__spin-block[_ngcontent-%COMP%]{border-color:Canvas highlight highlight highlight}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSpinnerComponent, [{
    type: Component,
    args: [{
      selector: 'nx-spinner',
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        '[class.nx-spinner--small]': 'size === "small"',
        '[class.nx-spinner--medium]': 'size === "medium"',
        '[class.nx-spinner--large]': 'size === "large"',
        '[class.nx-spinner--negative]': 'negative',
        '[attr.role]': '"status"',
        '[attr.aria-live]': 'ariaPoliteness',
        '[attr.aria-hidden]': '"true"'
      },
      standalone: true,
      template: "<div class=\"nx-spinner__spin-block\">\n    <div class=\"nx-spinner__edge-left\"></div>\n    <div class=\"nx-spinner__edge-right\"></div>\n</div>\n",
      styles: ["@keyframes spin{0%{transform:rotate(45deg)}to{transform:rotate(405deg)}}:host{position:relative;display:block}:host(.nx-spinner--small){width:16px;height:16px}:host(.nx-spinner--medium){width:24px;height:24px}:host(.nx-spinner--large){width:40px;height:40px}.nx-spinner__spin-block{width:100%;height:100%;transform:rotate(45deg);border-style:solid;border-radius:50%;border-color:var(--spinner-indicator-color) var(--spinner-background-color) var(--spinner-background-color) var(--spinner-background-color);animation-name:spin;animation-duration:.8s;animation-iteration-count:infinite;animation-timing-function:linear}:host(.nx-spinner--small) .nx-spinner__spin-block{border-width:1.6px}:host(.nx-spinner--medium) .nx-spinner__spin-block{border-width:2.4px}:host(.nx-spinner--large) .nx-spinner__spin-block{border-width:4px}.nx-spinner__edge-left,.nx-spinner__edge-right{width:12%;height:12%;border-radius:50%;background-color:var(--spinner-indicator-color);position:absolute;top:4%}:host(.nx-spinner--small) .nx-spinner__edge-left,:host(.nx-spinner--small) .nx-spinner__edge-right{width:10%;height:10%}.nx-spinner__edge-left{left:4%}.nx-spinner__edge-right{right:4%}:host(.nx-spinner--negative) .nx-spinner__spin-block{border-color:var(--spinner-negative-indicator-color) var(--spinner-negative-background-color) var(--spinner-negative-background-color) var(--spinner-negative-background-color)}:host(.nx-spinner--negative) .nx-spinner__edge-left,:host(.nx-spinner--negative) .nx-spinner__edge-right{background-color:var(--spinner-negative-indicator-color)}@media screen and (forced-colors: active){:host .nx-spinner__spin-block,:host.nx-spinner--negative .nx-spinner__spin-block{border-color:Canvas highlight highlight highlight}}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i1.LiveAnnouncer
  }], {
    size: [{
      type: Input
    }],
    negative: [{
      type: Input
    }],
    activationAnnouncement: [{
      type: Input
    }],
    completionAnnouncement: [{
      type: Input
    }],
    ariaPoliteness: [{
      type: Input
    }]
  });
})();
class NxSpinnerModule {
  static {
    this.ɵfac = function NxSpinnerModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSpinnerModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxSpinnerModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSpinnerModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, NxSpinnerComponent],
      exports: [NxSpinnerComponent],
      bootstrap: [],
      providers: []
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NxSpinnerComponent, NxSpinnerModule };
